import { useCallback, useMemo, useReducer } from 'react';
import usePharmacyLoad from './usePharmacyLoad';
import { getPrescriptions } from '../api/pharmacy';
import { getDispenses } from '../api/dispensing';
import { toInventoryList } from '../api/inventory-request';
import { mapList, mapPrescription } from '../utils/pharmacyMappers';
import { isDemoPrescriptionList } from '../utils/pharmacyFallbacks';
import { openDispensesByOrder } from '../utils/dispenseOrderLink';
import {
  DEFAULT_PRESCRIPTION_STATUS_FILTER,
  isCompletedPrescriptionStatus,
  isInProgressPrescriptionStatus,
  isPartialPrescriptionStatus,
  isPendingPrescriptionStatus,
  matchesPrescriptionStatusFilter,
} from '../utils/prescriptionStatus';
import {
  hasUnfilledRemainder,
  isFillablePrescription,
  isStalePrescription,
} from '../utils/prescriptionFulfilment';

export const DEFAULT_ROWS_PER_PAGE = 25;

/** Fields the free-text search matches — every one is present on a mapped row. */
const SEARCH_FIELDS = [
  'id',
  'order_number',
  'uhid',
  'patient_name',
  'doctor_name',
  'ward',
  'ward_name',
  'bed_no',
  'room_no',
];

const INITIAL_QUERY = {
  search: '',
  status: DEFAULT_PRESCRIPTION_STATUS_FILTER,
  flag: null,
  ward: '',
  page: 1,
  rowsPerPage: DEFAULT_ROWS_PER_PAGE,
};

/**
 * One reducer instead of a `useState` per control. Narrowing the result set always
 * has to send the reader back to page 1, and encoding that here means no caller can
 * forget it — the bug you get otherwise is an empty table on page 4 of 1.
 */
function queryReducer(state, action) {
  switch (action.type) {
    case 'search':
      return { ...state, search: action.value, page: 1 };
    case 'status':
      return { ...state, status: action.value, flag: null, page: 1 };
    case 'kpi':
      return { ...state, status: action.status, flag: action.flag || null, page: 1 };
    // Flag-only toggle (the stale sub-filter): narrows within the CURRENT status
    // selection instead of resetting it the way a KPI card does.
    case 'flag':
      return { ...state, flag: action.value || null, page: 1 };
    case 'ward':
      return { ...state, ward: action.value, page: 1 };
    case 'page':
      return { ...state, page: Math.max(1, action.value) };
    case 'rowsPerPage':
      return { ...state, rowsPerPage: action.value, page: 1 };
    case 'reset':
      return { ...INITIAL_QUERY };
    default:
      return state;
  }
}

function matchesSearch(row, term) {
  return SEARCH_FIELDS.some((field) => {
    const value = row[field];
    return value != null && String(value).toLowerCase().includes(term);
  });
}

/**
 * Data + derivation for the IPD Dispense screen, so the tab stays presentational.
 *
 * The list is fetched ONCE. `/get_ipd_prescriptions/` accepts no filter the screen
 * uses (status/search/sort/paging are all resolved client-side here), so re-fetching
 * an unbounded list on every filter change was pure waste.
 *
 * Counts are derived from the full list on purpose — they describe the ward's
 * workload, not the current view, so neither the search box nor the status dropdown
 * may move them.
 */
export default function useIpdPrescriptions() {
  const [query, dispatch] = useReducer(queryReducer, INITIAL_QUERY);
  const { data, loading, error, reload } = usePharmacyLoad(() => getPrescriptions(), []);

  const rows = useMemo(() => mapList(data, mapPrescription), [data]);

  /**
   * Orders that already have a dispense in flight, so the table can send the
   * pharmacist to it instead of offering Start/Continue Dispense — which reopened the
   * start page and created a *second* transaction every time.
   *
   * One extra request per visit, not per row. It never surfaces an error: if the
   * dispensing service is unreachable the screen still lists prescriptions, it just
   * cannot offer the resume shortcut.
   *
   * `limit` is the endpoint's maximum (`le=200`). It used to be omitted, which took
   * the server default of 50 newest transactions hospital-wide — so an order whose
   * dispense had scrolled past that window was offered Start Dispense again and got
   * a duplicate transaction. StartDispensePage does its own per-order check and is
   * the real guard; this only keeps the queue's button honest.
   */
  const { data: dispenseData } = usePharmacyLoad(
    () => getDispenses({ limit: 200 }).catch(() => []),
    [],
  );

  const openDispenseByOrder = useMemo(
    () => openDispensesByOrder(toInventoryList(dispenseData)),
    [dispenseData],
  );

  const counts = useMemo(() => {
    // One timestamp for the whole pass — per-row Date.now() would let a render
    // straddle a day boundary and count the same list two different ways.
    const now = Date.now();
    const acc = rows.reduce((next, row) => {
      if (isPendingPrescriptionStatus(row.status)) next.pending += 1;
      else if (isPartialPrescriptionStatus(row.status)) next.partial += 1;
      else if (isInProgressPrescriptionStatus(row.status)) next.inProgress += 1;
      // Checked BEFORE completed: an order the ward part-accepted carries a
      // finished status over an unfinished order, and counting it as completed
      // is what made the card disagree with the list it filters to.
      else if (hasUnfilledRemainder(row.status, row.fulfilment)) next.partial += 1;
      else if (isCompletedPrescriptionStatus(row.status)) next.completed += 1;
      if (row.has_alerts) next.alerts += 1;
      if (row.has_controlled) next.controlled += 1;
      if (isStalePrescription(row, now)) next.stale += 1;
      return next;
    }, { pending: 0, inProgress: 0, partial: 0, completed: 0, alerts: 0, controlled: 0, stale: 0 });
    return { ...acc, total: rows.length };
  }, [rows]);

  /**
   * True only when the payload actually carries controlled-drug fields. The card is
   * hidden otherwise: `/get_ipd_prescriptions/` serializes medicines without any
   * controlled marker today, so a "0" there would be an invention, not a measurement.
   */
  const controlledDataAvailable = useMemo(
    () => rows.some((row) => row.controlled_data_available),
    [rows],
  );

  const isDemoData = useMemo(() => isDemoPrescriptionList(rows), [rows]);

  /**
   * 'ACTIVE' is the default view and the one that has to be quantity-aware: an
   * order the ward part-accepted is stamped 'Received', which the status-only
   * predicate reads as done, so the row with an unfilled remainder vanished from
   * the only list anyone looks at. Every other option is a real backend status
   * and stays a plain status match.
   */
  const statusFiltered = useMemo(
    () => rows.filter((row) => (query.status === 'ACTIVE'
      ? isFillablePrescription(row.status, row.fulfilment)
      : matchesPrescriptionStatusFilter(row.status, query.status))),
    [rows, query.status],
  );

  const flagFiltered = useMemo(() => {
    if (query.flag === 'alerts') return statusFiltered.filter((row) => row.has_alerts);
    if (query.flag === 'controlled') return statusFiltered.filter((row) => row.has_controlled);
    if (query.flag === 'stale') {
      const now = Date.now();
      return statusFiltered.filter((row) => isStalePrescription(row, now));
    }
    return statusFiltered;
  }, [statusFiltered, query.flag]);

  const wardOptions = useMemo(() => {
    const names = [...new Set(rows.map((row) => row.ward_name).filter(Boolean))].sort();
    return [{ value: '', label: 'All wards' }, ...names.map((name) => ({ value: name, label: name }))];
  }, [rows]);

  const wardFiltered = useMemo(
    () => (query.ward ? flagFiltered.filter((row) => row.ward_name === query.ward) : flagFiltered),
    [flagFiltered, query.ward],
  );

  const filtered = useMemo(() => {
    const term = query.search.trim().toLowerCase();
    if (!term) return wardFiltered;
    return wardFiltered.filter((row) => matchesSearch(row, term));
  }, [wardFiltered, query.search]);

  const pageCount = Math.max(1, Math.ceil(filtered.length / query.rowsPerPage));
  // Clamp rather than reset: shrinking the result set must not strand the reader on
  // a page that no longer exists, but it also must not lose their place needlessly.
  const page = Math.min(query.page, pageCount);

  const pageRows = useMemo(() => {
    const start = (page - 1) * query.rowsPerPage;
    return filtered.slice(start, start + query.rowsPerPage);
  }, [filtered, page, query.rowsPerPage]);

  const setSearch = useCallback((value) => dispatch({ type: 'search', value }), []);
  const setStatus = useCallback((value) => dispatch({ type: 'status', value }), []);
  const setKpi = useCallback((status, flag = null) => dispatch({ type: 'kpi', status, flag }), []);
  const setFlag = useCallback((value) => dispatch({ type: 'flag', value }), []);
  const setWard = useCallback((value) => dispatch({ type: 'ward', value }), []);
  const setPage = useCallback((value) => dispatch({ type: 'page', value }), []);
  const setRowsPerPage = useCallback((value) => dispatch({ type: 'rowsPerPage', value }), []);
  const resetFilters = useCallback(() => dispatch({ type: 'reset' }), []);

  const isFiltered = query.search.trim() !== ''
    || query.status !== INITIAL_QUERY.status
    || Boolean(query.flag)
    || query.ward !== '';

  return {
    // data
    rows,
    filtered,
    pageRows,
    counts,
    openDispenseByOrder,
    controlledDataAvailable,
    isDemoData,
    // request state
    loading,
    error,
    reload,
    // query state
    search: query.search,
    status: query.status,
    flag: query.flag,
    ward: query.ward,
    wardOptions,
    page,
    rowsPerPage: query.rowsPerPage,
    isFiltered,
    setSearch,
    setStatus,
    setKpi,
    setFlag,
    setWard,
    setPage,
    setRowsPerPage,
    resetFilters,
  };
}
